import React, { useState, useEffect } from "react";
import { FiUsers, FiCalendar, FiEye, FiExternalLink, FiCopy } from "react-icons/fi";
import API from "@/services/api";
import toast from "react-hot-toast";

const LINKEDIN_FEED_URL = "https://api.jobego.co/feed/linkedin.xml";

const Dashboard = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await API.post("/post/search", {
        channel_name: "Linkedin",
        status: "active",
        limit: 100,
      });
      if (res.ok) {
        setPosts(res.data || []);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const daysSince = (date) => {
    if (!date) return 0;
    return Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Feed URL */}
      <div className="flex items-center gap-4">
        <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 flex items-center gap-3">
          <code className="flex-1 text-sm font-mono text-blue-700 truncate">{LINKEDIN_FEED_URL}</code>
          <button
            onClick={() => { navigator.clipboard.writeText(LINKEDIN_FEED_URL); toast.success("Copied"); }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Copy URL"
          >
            <FiCopy className="w-4 h-4 text-gray-500" />
          </button>
          <a
            href={LINKEDIN_FEED_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Open feed"
          >
            <FiExternalLink className="w-4 h-4 text-gray-500" />
          </a>
        </div>
      </div>

      {/* Active Posts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900">Active LinkedIn Posts ({posts.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">Organization</th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <FiEye className="w-3.5 h-3.5" />
                    Views
                  </div>
                </th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <FiUsers className="w-3.5 h-3.5" />
                    Candidates
                  </div>
                </th>
                <th className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <FiCalendar className="w-3.5 h-3.5" />
                    Active Since
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No active LinkedIn posts
                  </td>
                </tr>
              ) : (
                posts.map((post) => {
                  const age = daysSince(post.created_at);
                  return (
                    <tr key={post._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">{post.job_title}</span>
                        <p className="text-xs text-gray-500">{post.job_location || "—"}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{post.organization_name || "—"}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{post.viewed_count || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{post.applied_count || 0}</td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {age} days
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
